/**
 * 多标签页（工单 #1249）的纯状态逻辑，不依赖 React，便于单测。
 *
 * 规则：
 * - 一个左侧末级菜单对应一个标签，标签 key = 菜单 path。
 * - 在标签内跳到非菜单页（详情/编辑）时留在当前标签，只更新它的 path。
 * - 首页标签固定，不能关闭。
 * - 每个标签各记一份来时的路（trail），页面里的「返回」只在本标签里退，不走共用的浏览器历史。
 */

export interface PageTab {
  /** 菜单 path */
  key: string;
  /** 该标签最后停留的地址（pathname + search） */
  path: string;
  /** 在本标签里走过的上一页、上上页……，最后一个是「返回」要去的地址 */
  trail: string[];
}

export interface TabsState {
  tabs: PageTab[];
  activeKey: string;
  /** 最近使用顺序，最后一个是最近一次 */
  mru: string[];
}

export const MAX_TABS = 15;
export const MAX_TRAIL = 20;

export const initialTabsState = (homeKey: string): TabsState => ({
  tabs: [{ key: homeKey, path: homeKey, trail: [] }],
  activeKey: homeKey,
  mru: [homeKey],
});

/** 菜单 path 最长前缀匹配（直接打开详情页地址时用来找归属菜单） */
const longestPrefixMenu = (pathname: string, menuKeys: Iterable<string>): string | null => {
  let best: string | null = null;
  for (const key of menuKeys) {
    if ((pathname === key || pathname.startsWith(`${key}/`)) && (!best || key.length > best.length)) {
      best = key;
    }
  }
  return best;
};

/**
 * 当前地址该归到哪个标签。返回 null 表示暂不归档（页面照常显示，但不进任何标签）。
 *
 * @param settled 本次挂载后是否已经成功归档过；没归档过时（刷新、切换模式的瞬间）
 *                不把未知地址塞进当前标签，避免覆盖它原来的位置。
 */
export const resolveTabKey = (
  state: TabsState,
  pathname: string,
  fullPath: string,
  menuKeys: ReadonlySet<string>,
  settled: boolean,
): string | null => {
  const isOpen = (key: string) => state.tabs.some((tab) => tab.key === key);

  if (menuKeys.has(pathname) && (isOpen(pathname) || state.tabs.length < MAX_TABS)) {
    return pathname;
  }

  const active = state.tabs.find((tab) => tab.key === state.activeKey);
  if (active?.path === fullPath) return active.key;
  const owner = state.tabs.find((tab) => tab.path === fullPath);
  if (owner) return owner.key;

  if (settled) return active ? active.key : null;

  const prefix = longestPrefixMenu(pathname, menuKeys);
  if (prefix && (isOpen(prefix) || state.tabs.length < MAX_TABS)) return prefix;
  return null;
};

export const pathnameOf = (fullPath: string) => fullPath.split('?')[0];

/**
 * 标签里要留着的页面（按 pathname）：当前停留的页 + 来时路上的每一页。
 * 从列表进详情时列表页不卸载，返回时原样显示，筛选和页码都在。
 */
export const keptPages = (tab: PageTab): string[] =>
  Array.from(new Set([tab.path, ...tab.trail].map(pathnameOf)));

/**
 * 标签里换了一个地址后，它的来时路怎么变：
 * - 回到菜单页：清空，菜单页没有上一页
 * - 回到上一页（「返回」或浏览器后退）：退一步
 * - 点进新页面：把当前页记下来；只改查询条件（翻页、筛选）不记
 */
const moveTab = (tab: PageTab, fullPath: string, pushed: boolean): PageTab => {
  if (fullPath === tab.path) return tab;
  let trail = tab.trail;
  if (pathnameOf(fullPath) === tab.key) trail = [];
  else if (fullPath === trail[trail.length - 1]) trail = trail.slice(0, -1);
  else if (pushed && pathnameOf(fullPath) !== pathnameOf(tab.path)) trail = [...trail, tab.path].slice(-MAX_TRAIL);
  return { ...tab, path: fullPath, trail };
};

/**
 * 激活（必要时新开）标签，并记下它当前停留的地址。
 * @param pushed 这次地址变化是不是新开一页（浏览器的 PUSH），替换和后退不算
 */
export const activateTab = (state: TabsState, key: string, fullPath: string, pushed = false): TabsState => {
  const exists = state.tabs.some((tab) => tab.key === key);
  const tabs = exists
    ? state.tabs.map((tab) => (tab.key === key ? moveTab(tab, fullPath, pushed) : tab))
    : [...state.tabs, { key, path: fullPath, trail: [] }];
  return {
    tabs,
    activeKey: key,
    mru: [...state.mru.filter((k) => k !== key), key],
  };
};

/** 页面里点「返回」要去的地址：本标签的上一页，没有就回菜单页 */
export const backTarget = (state: TabsState, key: string): string | null => {
  const tab = state.tabs.find((t) => t.key === key);
  if (!tab) return null;
  return tab.trail[tab.trail.length - 1] ?? tab.key;
};

/** 关闭标签。关的是当前标签时切到最近一次用过的标签。首页不能关。 */
export const closeTab = (state: TabsState, key: string, homeKey: string): TabsState => {
  if (key === homeKey || !state.tabs.some((tab) => tab.key === key)) return state;
  const tabs = state.tabs.filter((tab) => tab.key !== key);
  const mru = state.mru.filter((k) => k !== key);
  const activeKey = state.activeKey === key ? mru[mru.length - 1] ?? homeKey : state.activeKey;
  return { tabs, activeKey, mru };
};

/** 关闭除首页和 keepKey 以外的所有标签 */
export const closeOtherTabs = (state: TabsState, keepKey: string | null, homeKey: string): TabsState =>
  state.tabs
    .filter((tab) => tab.key !== keepKey)
    .reduce((s, tab) => closeTab(s, tab.key, homeKey), state);

/**
 * 点左侧菜单时要跳去哪。
 * - 点的就是当前标签：回到菜单首页（比如从详情回到列表）
 * - 已打开的标签：回到它最后停留的地址
 * - 没打开且已满：返回 null，由调用方提示
 */
export const menuTarget = (state: TabsState, key: string): string | null => {
  if (key === state.activeKey) return key;
  const tab = state.tabs.find((t) => t.key === key);
  if (tab) return tab.path;
  return state.tabs.length < MAX_TABS ? key : null;
};

/** 从会话存储里恢复的数据不可信，形状不对就丢弃 */
export const parseTabsState = (raw: string | null, homeKey: string): TabsState => {
  if (!raw) return initialTabsState(homeKey);
  try {
    const data = JSON.parse(raw) as Partial<TabsState>;
    const tabs: PageTab[] = Array.isArray(data.tabs)
      ? data.tabs
        .filter((t) => typeof t?.key === 'string' && typeof t?.path === 'string')
        .map((t) => ({
          key: t.key,
          path: t.path,
          trail: Array.isArray(t.trail)
            ? t.trail.filter((p): p is string => typeof p === 'string').slice(-MAX_TRAIL)
            : [],
        }))
      : [];
    if (!tabs.some((t) => t.key === homeKey)) tabs.unshift({ key: homeKey, path: homeKey, trail: [] });
    const keys = new Set(tabs.map((t) => t.key));
    const mru = Array.isArray(data.mru) ? data.mru.filter((k) => keys.has(k)) : [];
    const activeKey = typeof data.activeKey === 'string' && keys.has(data.activeKey) ? data.activeKey : homeKey;
    return { tabs: tabs.slice(0, MAX_TABS), activeKey, mru: mru.length ? mru : [activeKey] };
  } catch {
    return initialTabsState(homeKey);
  }
};

/**
 * 菜单权限变了以后，去掉已经不在菜单里的标签。
 * 当前标签不动：它正显示着页面，关掉会把这个页面挪进别的标签。
 */
export const pruneTabs = (state: TabsState, menuKeys: ReadonlySet<string>, homeKey: string): TabsState => {
  const stale = state.tabs.filter((t) => t.key !== homeKey && t.key !== state.activeKey && !menuKeys.has(t.key));
  return stale.reduce((s, t) => closeTab(s, t.key, homeKey), state);
};
